import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { List, LayoutGrid, ArrowLeft } from "lucide-react";
import { CandidateCard, CandidatePanel } from "./CandidateComponents";
import FilterModal from "./FilterModal";
import AddToSequenceModal from "./sequencing/AddToSequenceModal.jsx";
import PreviewModal from "./sequencing/PreviewModal.jsx";
import Pagination from "../../components/shared_components/Pagination";
import usePagination from "../hooks/usePagination";


// -- main results page ------------------------------------------------------
const SourcingResults = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const query = location.state?.query || "";
    const searchId = location.state?.search_id || null;
    const canonicalQuery = location.state?.canonical_query || null;
    const jobId = location.state?.job_id || null;
    const jobRole = location.state?.job_role || null;
    const orgId = useSelector(state => state.userSlice?.org_id);
    const recruiterName = useSelector(state => state.userSlice?.name) || localStorage.getItem("username") || "";
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [displayedQuery, setDisplayedQuery] = useState(location.state?.query || "");
    const [similarSourceCandidate, setSimilarSourceCandidate] = useState(location.state?.source_candidate || null);
    const similarBackRef = useRef(null);
    const [isInSimilarMode, setIsInSimilarMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState("list");
    const [shortlistedMap, setShortlistedMap] = useState({});
    const [hiddenMap, setHiddenMap] = useState({});
    const [toast, setToast] = useState(null);
    const [showViewedOnly, setShowViewedOnly] = useState(false);
    const [saveModal, setSaveModal] = useState(false);
    const [aliasInput, setAliasInput] = useState("");
    const [aliasSaved, setAliasSaved] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [editFiltersOpen, setEditFiltersOpen] = useState(false);
    const [addToSeqCandidates, setAddToSeqCandidates] = useState(null);
    const [previewState, setPreviewState] = useState(null); // { candidates, sequence }
    const [enrollResult, setEnrollResult] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
    const bulkStatusRef = useRef(null);

    // Single-candidate enroll requires an email; route no-email candidates through
    // the bulk email-gate (reveal / add / remove) before sequence selection.
    // Take the row from the list, not the object the panel was opened with, so an email
    // added in the panel is on it — the gate reads these fields to decide readiness.
    const startEnroll = (c) => {
        const row = result?.candidates?.find(r => r.candidate_id === c.candidate_id);
        setAddToSeqCandidates([row || c]);
    };

    // A contact value revealed or added in the panel belongs on the row too, so the
    // enroll gate and the card behind the panel both see it without a refetch.
    const patchCandidateContact = (candidateId, changes) => {
        setResult(prev => prev && ({
            ...prev,
            candidates: prev.candidates.map(c =>
                c.candidate_id === candidateId ? { ...c, ...changes } : c
            ),
        }));
        setSelectedCandidate(prev =>
            prev?.candidate_id === candidateId ? { ...prev, ...changes } : prev
        );
    };
    // Tracks the navigation (location.key) we've already fetched for, so a single
    // search fires one /find — even under StrictMode's double-invoked effects.
    const fetchedKeyRef = useRef(null);

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(null), 3500);
    };

    const BULK_STATUS_OPTIONS = [
        { value: "not_contacted",  label: "Not Contacted",  color: "#93c5fd" },
        { value: "email_sent",     label: "Email Sent",     color: "#0d9488" },
        { value: "interested",     label: "Interested",     color: "#a78bfa" },
        { value: "interviewing",   label: "Interviewing",   color: "#fbbf24" },
        { value: "rejected",       label: "Rejected",       color: "#ef4444" },
        { value: "not_interested", label: "Not Interested", color: "#9ca3af" },
    ];

    useEffect(() => {
        if (result?.candidates) {
            setShortlistedMap(
                Object.fromEntries(result.candidates.map(c => [c.candidate_id, c.is_shortlisted ?? false]))
            );
            setHiddenMap(
                Object.fromEntries(result.candidates.map(c => [c.candidate_id, c.is_hidden ?? false]))
            );
        }
    }, [result]);

    const handleShortlistToggle = async (candidate) => {
        const currentOrgId = localStorage.getItem("current_org_id");
        const adminId = localStorage.getItem("org_admin_id");
        const isShortlisted = shortlistedMap[candidate.candidate_id];
        const currentSearchId = result?.search_id || searchId;
        try {
            if (isShortlisted) {
                await axios.delete(
                    `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/shortlist/${candidate.candidate_id}`,
                    { params: { organisation_id: currentOrgId, recruiter_id: adminId, recruiter_name: recruiterName, search_id: currentSearchId } }
                );
                setShortlistedMap(prev => ({ ...prev, [candidate.candidate_id]: false }));
            } else {
                await axios.post(
                    `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/shortlist`,
                    { candidate_id: candidate.candidate_id, organisation_id: currentOrgId, recruiter_id: adminId, recruiter_name: recruiterName, search_id: currentSearchId }
                );
                setShortlistedMap(prev => ({ ...prev, [candidate.candidate_id]: true }));
            }
        } catch (err) {
            if (err.response?.status === 404) setShortlistedMap(prev => ({ ...prev, [candidate.candidate_id]: false }));
        }
    };

    const handleHideToggle = async (candidate) => {
        const currentlyHidden = hiddenMap[candidate.candidate_id];
        const currentOrgId = localStorage.getItem("current_org_id");
        const adminId = localStorage.getItem("org_admin_id");
        setHiddenMap(prev => ({ ...prev, [candidate.candidate_id]: !currentlyHidden }));
        try {
            if (currentlyHidden) {
                await axios.delete(
                    `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/hide/${candidate.candidate_id}`,
                    { params: { recruiter_id: adminId, organisation_id: currentOrgId } }
                );
            } else {
                await axios.post(
                    `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/hide`,
                    { candidate_id: candidate.candidate_id, recruiter_id: adminId, organisation_id: currentOrgId }
                );
            }
        } catch {
            setHiddenMap(prev => ({ ...prev, [candidate.candidate_id]: currentlyHidden }));
        }
    };

    const handleSelect = (candidate, checked) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            checked ? next.add(candidate.candidate_id) : next.delete(candidate.candidate_id);
            return next;
        });
    };

    const handleBatchShortlist = async () => {
        const currentOrgId = localStorage.getItem("current_org_id");
        const adminId = localStorage.getItem("org_admin_id");
        const currentSearchId = result?.search_id || searchId;
        const ids = [...selectedIds];
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/shortlist/batch`,
                { candidate_ids: ids, organisation_id: currentOrgId, recruiter_id: adminId, recruiter_name: recruiterName, search_id: currentSearchId }
            );
            setSelectedIds(new Set());
            data.shortlisted.forEach(id => setShortlistedMap(prev => ({ ...prev, [id]: true })));
            showToast(`${data.shortlisted.length} shortlisted`);
        } catch (err) {
            showToast("Failed to shortlist. Please try again.");
        }
    };

    const handleSaveSearch = async () => {
        if (!aliasInput.trim()) return;
        if (!result?.search_id) {
            setSaveError("Search could not be saved. Please try your search again.");
            return;
        }
        setSaveError("");
        try {
            await axios.patch(
                `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/searches/${result.search_id}`,
                { alias: aliasInput.trim() }
            );
            setAliasSaved(true);
            setSaveModal(false);
        } catch (err) {
            console.error(err);
            setSaveError("Failed to save. Please try again.");
        }
    };

    const handleFilterSearch = async (updatedQuery) => {
        setEditFiltersOpen(false);
        setLoading(true);
        setError(null);
        try {
            const currentOrgId = localStorage.getItem("current_org_id");
            const currentSearchId = result?.search_id || searchId;
            const { data } = await axios.post(
                `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/find`,
                {
                    query,
                    canonical_query: updatedQuery,
                    organisation_id: currentOrgId,
                    recruiter_id: localStorage.getItem("org_admin_id"),
                    search_id: currentSearchId,
                    // Status stamping is per-job on the backend; without this the
                    // re-run would decorate rows with org-wide statuses instead.
                    job_id: result?.job_id || jobId,
                }
            );
            setResult(data);
        } catch (err) {
            setError("Search failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };



    const handleFindSimilar = async (candidate) => {
        const recruiterId = localStorage.getItem("org_admin_id");
        const currentOrgId = orgId || localStorage.getItem("current_org_id");
        const parentSearchId = result?.search_id || searchId || null;
        // A "similar" search inherits the same job role — no orphan searches.
        const inheritJobId = result?.job_id || jobId;
        const inheritJobRole = result?.job_role || jobRole;

        similarBackRef.current = {
            result,
            displayedQuery,
            aliasSaved,
            sourceCandidateInfo: similarSourceCandidate,
        };
        setIsInSimilarMode(true);

        const skills = candidate.skills || [];
        const yearsExp = candidate.years_of_experience;
        const loc = candidate.location_city || candidate.location_state || candidate.location_country || null;

        const builtQuery = {
            roles: [candidate.current_title].filter(Boolean),
            skills_required: skills.slice(0, 5),
            skills_nice_to_have: skills.slice(5, 10),
            locations: loc ? [loc] : [],
            seniority: candidate.seniority || null,
            experience_min: yearsExp != null ? Math.max(0, yearsExp - 3) : null,
            experience_max: yearsExp != null ? yearsExp + 3 : null,
            companies: [],
            limit: 10,
        };
        const newDisplayQuery = `Similar profiles to ${candidate.full_name}${candidate.current_title ? ` – ${candidate.current_title}` : ""}`;

        setSelectedCandidate(null);
        setResult(null);
        setError(null);
        setSelectedIds(new Set());
        setDisplayedQuery(newDisplayQuery);
        setSimilarSourceCandidate({ name: candidate.full_name, title: candidate.current_title });
        setAliasSaved(false);
        setLoading(true);

        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/find`,
                {
                    query: newDisplayQuery,
                    canonical_query: builtQuery,
                    organisation_id: currentOrgId,
                    recruiter_id: recruiterId,
                    parent_search_id: parentSearchId,
                    job_id: inheritJobId,
                    job_role: inheritJobRole,
                }
            );
            setResult(data);
            setShortlistedMap(Object.fromEntries(
                (data.candidates || []).map(c => [c.candidate_id, c.is_shortlisted ?? false])
            ));
        } catch {
            setError("Similar search failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleBackToOriginal = () => {
        const saved = similarBackRef.current;
        if (!saved) return;
        setResult(saved.result);
        setDisplayedQuery(saved.displayedQuery);
        setSimilarSourceCandidate(saved.sourceCandidateInfo);
        setAliasSaved(saved.aliasSaved);
        setShortlistedMap(Object.fromEntries(
            (saved.result?.candidates || []).map(c => [c.candidate_id, c.is_shortlisted ?? false])
        ));
        setSelectedIds(new Set());
        setError(null);
        similarBackRef.current = null;
        setIsInSimilarMode(false);
    };

    // Close bulk dropdowns on outside click
    useEffect(() => {
        const handler = (e) => {
            if (bulkStatusRef.current && !bulkStatusRef.current.contains(e.target))
                setBulkStatusOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const displayedCandidates = result
        ? (showViewedOnly ? result.candidates.filter(c => c.is_viewed) : result.candidates)
        : [];

    const {
        currentPage,
        itemsPerPage,
        pageItems: pagedCandidates,
        handlePageChange,
        handleItemsPerPageChange,
    } = usePagination(displayedCandidates, { resetKey: `${result?.search_id ?? ""}|${showViewedOnly}` });

    // Header checkbox acts on the rows you can actually see; selecting the whole
    // result set across pages stays available from the bulk bar.
    const allOnPageSelected =
        pagedCandidates.length > 0 && pagedCandidates.every(c => selectedIds.has(c.candidate_id));

    const handleSelectPage = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            pagedCandidates.forEach(c =>
                allOnPageSelected ? next.delete(c.candidate_id) : next.add(c.candidate_id)
            );
            return next;
        });
    };

    const handleSelectAll = () => {
        if (selectedIds.size === displayedCandidates.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(displayedCandidates.map(c => c.candidate_id)));
        }
    };

    const handleBulkStatus = async (newStatus) => {
        const recruiterId = localStorage.getItem("org_admin_id");
        const currentOrgId = localStorage.getItem("current_org_id");
        const selected = displayedCandidates.filter(c => selectedIds.has(c.candidate_id));
        const statusLabel = BULK_STATUS_OPTIONS.find(o => o.value === newStatus)?.label;
        setBulkStatusOpen(false);

        // Pipeline status is a standalone outreach stage — shortlisting/saving
        // have their own dedicated bulk actions and are not coupled here.
        // Pipeline event + activity log per candidate (same path as individual)
        await Promise.allSettled([
            ...selected.map(c =>
                axios.post(`${process.env.REACT_APP_SOURCING_API_URL}/sourcing/pipeline`, {
                    candidate_id: c.candidate_id,
                    recruiter_id: recruiterId,
                    organisation_id: currentOrgId,
                    action: newStatus,
                    // Per-role status: this search is tied to a job, so file it there.
                    job_id: jobId || result?.job_id || null,
                })
            ),
            ...selected.map(c =>
                axios.post(`${process.env.REACT_APP_SOURCING_API_URL}/sourcing/activity`, {
                    action: "status_changed",
                    candidate_id: c.candidate_id,
                    recruiter_id: recruiterId,
                    organisation_id: currentOrgId,
                    metadata: {
                        candidate_name: c.full_name,
                        new_status: newStatus,
                        new_status_label: statusLabel,
                    },
                })
            ),
        ]);

        setSelectedIds(new Set());
    };

    // Reset on every navigation (handles navigating to the same route)
    useEffect(() => {
        setResult(null);
        setError(null);
        setSelectedCandidate(null);
        setSelectedIds(new Set());
        setDisplayedQuery(location.state?.query || "");
        setSimilarSourceCandidate(location.state?.source_candidate || null);
        similarBackRef.current = null;
        setIsInSimilarMode(false);
    }, [location.key]);

    // Primary fetch — waits for orgId so is_shortlisted is properly seeded from the DB
    useEffect(() => {
        if (!query) { navigate("/org-admin/sourcing"); return; }
        if (fetchedKeyRef.current === location.key) return;
        if (!orgId) return; // wait for updateLastLogin to populate Redux store
        fetchedKeyRef.current = location.key;
        fetchCandidates(orgId);
    }, [query, orgId, location.key]);

    // Fallback: if updateLastLogin API never resolves, fetch after 5s without shortlist stamps
    useEffect(() => {
        if (!query) return;
        const timer = setTimeout(() => {
            if (fetchedKeyRef.current === location.key) return;
            fetchedKeyRef.current = location.key;
            fetchCandidates(localStorage.getItem("current_org_id"));
        }, 5000);
        return () => clearTimeout(timer);
    }, [query, location.key]);

    const fetchCandidates = async (currentOrgId) => {
        setLoading(true);
        try {
            if (searchId) {
                // load snapshot — exact candidates from that search
                const { data } = await axios.get(
                    `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/searches/${searchId}`,
                    { params: { organisation_id: currentOrgId, recruiter_id: localStorage.getItem("org_admin_id") } }
                );
                setResult({
                    total: data.result_count,
                    candidates: data.candidates,
                    parsed_query: data.parsed_query,
                    source_breakdown: data.source_breakdown,
                    job_id: data.job_id,
                    job_role: data.job_role,
                });
            } else {
                // fresh search — tied to the selected job role
                const { data } = await axios.post(
                    `${process.env.REACT_APP_SOURCING_API_URL}/sourcing/find`,
                    { query, organisation_id: currentOrgId, recruiter_id: localStorage.getItem("org_admin_id"), canonical_query: canonicalQuery, job_id: jobId, job_role: jobRole }
                );
                setResult(data);
            }
        } catch (err) {
            setError("Search failed. Please try again.");
            console.error(err.response?.data);
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-white/[0.03]">

            {/* -- top bar ---------------------------------------- */}
            <div className="bg-white dark:bg-white/5 border-b border-gray-100 dark:border-gray-700/50 px-6 py-4 flex items-center gap-4">
                {isInSimilarMode ? (
                    <button
                        onClick={handleBackToOriginal}
                        className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-all shrink-0"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="truncate max-w-[180px]">{similarBackRef.current?.displayedQuery}</span>
                    </button>
                ) : (
                    <button
                        onClick={() => navigate(-1)}
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-all"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                )}
                <div className="flex-1 px-5 py-2.5 rounded-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-white/[0.03] text-sm text-gray-600 dark:text-gray-300 truncate">
                    {displayedQuery}
                </div>
                {(jobRole || result?.job_role) && (
                    <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-50 dark:bg-purple-500/15 border border-purple-100 dark:border-purple-500/30 text-xs font-medium text-purple-700 dark:text-purple-300 max-w-[220px] truncate" title={jobRole || result?.job_role}>
                        {jobRole || result?.job_role}
                    </span>
                )}
                {result && (result.search_id || searchId) && (
                    <div className="flex items-center gap-2 shrink-0">
                        {[
                            { label: "Shortlist", path: "/org-admin/sourcing/shortlist" },
                            { label: "Saved", path: "/org-admin/sourcing/saved" },
                            { label: "Contacted", path: "/org-admin/sourcing/contacted" },
                        ].map(({ label, path }) => (
                            <button
                                key={label}
                                onClick={() => navigate(path, { state: { search_id: result?.search_id || searchId, job_id: result?.job_id || jobId, job_role: result?.job_role || jobRole, searchName: displayedQuery } })}
                                className="px-4 py-2 rounded-full text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-purple-400 hover:text-purple-600 transition-all"
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}
                {result && (
                    <button
                        onClick={() => setEditFiltersOpen(true)}
                        className="shrink-0 px-4 py-2 rounded-full text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-purple-400 hover:text-purple-600 transition-all"
                    >
                        Edit Filters
                    </button>
                )}
            </div>


            {/* -- content ---------------------------------------- */}
            <div className="w-full px-6 py-6">

                {/* loading */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-24 gap-3">
                        <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-gray-400 dark:text-gray-500">Finding candidates...</p>
                    </div>
                )}

                {/* error */}
                {error && !loading && (
                    <div className="text-center py-24">
                        <p className="text-sm text-red-400">{error}</p>
                        <button
                            onClick={() => fetchCandidates(orgId)}
                            className="mt-4 px-4 py-2 rounded-full text-sm text-purple-600 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30 hover:bg-purple-50"
                        >
                            Try again
                        </button>
                    </div>
                )}

                {/* results */}
                {result && !loading && (
                    <>
                        {/* matches bar */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={allOnPageSelected}
                                    ref={el => {
                                        if (el) el.indeterminate =
                                            !allOnPageSelected && pagedCandidates.some(c => selectedIds.has(c.candidate_id));
                                    }}
                                    onChange={handleSelectPage}
                                    className="accent-purple-600 cursor-pointer w-4 h-4"
                                    title="Select all on this page"
                                />
                                <h2 className="text-base font-semibold text-gray-800 dark:text-white">
                                    Matches ({result.total})
                                </h2>

                                {/* <button
                                    onClick={() => setShowViewedOnly(v => !v)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                        showViewedOnly
                                            ? "bg-purple-100 text-purple-600 border-purple-200"
                                            : "text-gray-400 border-gray-200 hover:text-gray-600"
                                    }`}
                                >
                                    Previously viewed
                                </button> */}
                                <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-600 rounded-lg p-0.5">
                                    <button
                                        onClick={() => setViewMode("list")}
                                        className={`p-1.5 rounded-md transition-all ${
                                            viewMode === "list"
                                                ? "bg-purple-100 text-purple-600"
                                                : "text-gray-400 hover:text-gray-600"
                                        }`}
                                    >
                                        <List className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => setViewMode("grid")}
                                        className={`p-1.5 rounded-md transition-all ${
                                            viewMode === "grid"
                                                ? "bg-purple-100 text-purple-600"
                                                : "text-gray-400 hover:text-gray-600"
                                        }`}
                                    >
                                        <LayoutGrid className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                            {!searchId && (
                                aliasSaved ? (
                                    <span className="text-sm text-green-600 dark:text-green-400 font-medium">Saved âœ"</span>
                                ) : (
                                    <button
                                        onClick={() => setSaveModal(true)}
                                        className="px-4 py-2 rounded-full text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-purple-400 hover:text-purple-600 transition-all"
                                    >
                                        Save Search
                                    </button>
                                )
                            )}
                            {/* <button
                                onClick={() => navigate("/org-admin/sourcing/saved", { state: { query, search_id: result?.search_id || searchId } })}
                                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-purple-400 hover:text-purple-600 transition-all"
                            >
                                View Saved
                            </button>
                            <button
                                onClick={() => navigate("/org-admin/sourcing/shortlist", { state: { query, search_id: result?.search_id || searchId } })}
                                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
                                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                            >
                                View Shortlisted
                            </button> */}
                        </div>

                        </div>

                        {/* bulk action bar */}
                        {selectedIds.size > 0 && (
                            <div className="flex items-center gap-3 mb-3 px-4 py-2.5 bg-white dark:bg-white/5 border border-purple-100 dark:border-purple-500/30 rounded-xl shadow-sm">
                                <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                                    {selectedIds.size} selected
                                </span>
                                <button
                                    onClick={handleSelectAll}
                                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline-offset-2 hover:underline"
                                >
                                    {selectedIds.size === displayedCandidates.length ? "Deselect all" : `Select all ${displayedCandidates.length}`}
                                </button>
                                <div className="flex-1" />
                                <button
                                    onClick={() => handleBatchShortlist()}
                                    className="px-4 py-1.5 rounded-lg text-sm font-medium text-white transition-all"
                                    style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                >
                                    Shortlist ({selectedIds.size})
                                </button>
                                <button
                                    onClick={() => setAddToSeqCandidates(displayedCandidates.filter(c => selectedIds.has(c.candidate_id)))}
                                    className="px-4 py-1.5 rounded-lg text-sm font-medium text-white transition-all"
                                    style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                                >
                                    + Add to Outreach ({selectedIds.size})
                                </button>
                                <div className="relative" ref={bulkStatusRef}>
                                    <button
                                        onClick={() => setBulkStatusOpen(o => !o)}
                                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-white/5 hover:border-purple-400 transition-all text-sm text-gray-700 dark:text-gray-300"
                                    >
                                        Change status
                                        <svg className="w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    {bulkStatusOpen && (
                                        <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-[#1B1524] border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg z-20 py-1 overflow-hidden">
                                            {BULK_STATUS_OPTIONS.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => handleBulkStatus(opt.value)}
                                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-all text-left"
                                                >
                                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: opt.color }} />
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => setSelectedIds(new Set())}
                                    className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 px-2"
                                >
                                    ✕
                                </button>
                            </div>
                        )}

                        {/* similar search context banner */}
                        {similarSourceCandidate && (
                            <div className="flex items-center gap-2 mb-3 px-4 py-2.5 bg-purple-50 dark:bg-purple-500/15 border border-purple-100 dark:border-purple-500/30 rounded-xl text-sm text-purple-700 dark:text-purple-300">
                                <svg className="w-4 h-4 shrink-0 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                Showing profiles similar to{" "}
                                <span className="font-medium">{similarSourceCandidate.name}</span>
                                {similarSourceCandidate.title && (
                                    <span className="text-purple-400">· {similarSourceCandidate.title}</span>
                                )}
                            </div>
                        )}

                        {/* a short page: what a looser filter would find */}
                        {result.notice?.message && (
                            <div className="mb-3 px-4 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                                {result.notice.message}
                            </div>
                        )}

                        {/* candidate list */}
                        {result.total === 0 ? (
                            <div className="text-center py-24">
                                <p className="text-gray-400 dark:text-gray-500">
                                    {result.no_results_hint?.message || "No candidates found. Try a broader search."}
                                </p>
                            </div>
                        ) : (
                            <>
                            <div className={`bg-white rounded-2xl border border-gray-100 overflow-hidden ${
                                viewMode === "grid"
                                    ? "grid grid-cols-2 gap-px bg-gray-100"
                                    : ""
                            }`}>
                                {pagedCandidates.map((candidate) => (
                                    <CandidateCard
                                        key={candidate.candidate_id}
                                        candidate={candidate}
                                        onClick={() => setSelectedCandidate(candidate)}
                                        shortlisted={shortlistedMap[candidate.candidate_id] ?? false}
                                        onShortlist={handleShortlistToggle}
                                        hidden={hiddenMap[candidate.candidate_id] ?? false}
                                        onHide={handleHideToggle}
                                        selected={selectedIds.has(candidate.candidate_id)}
                                        isSelected={selectedIds.has(candidate.candidate_id)}
                                        onSelect={handleSelect}
                                    />
                                ))}
                            </div>
                            <div className="pt-6">
                                <Pagination
                                    currentPage={currentPage}
                                    totalItems={displayedCandidates.length}
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={handlePageChange}
                                    onItemsPerPageChange={handleItemsPerPageChange}
                                    label="Candidates"
                                />
                            </div>
                            </>
                        )}
                    </>
                )}
            </div>
            {selectedCandidate && (
                <CandidatePanel
                    candidate={selectedCandidate}
                    onClose={() => setSelectedCandidate(null)}
                    shortlisted={shortlistedMap[selectedCandidate.candidate_id] ?? false}
                    onShortlist={handleShortlistToggle}
                    onEnroll={startEnroll}
                    onContactUpdate={patchCandidateContact}
                    onFindSimilar={handleFindSimilar}
                    jobId={result?.job_id || jobId}
                    // Scheduled candidates have moved on to Interview Management. These
                    // results are held client-side, so drop the rows rather than re-running
                    // the search — a re-run would spend credits to return the same people.
                    onScheduled={(ids) => {
                        const gone = new Set(ids);
                        setResult(prev => prev && ({
                            ...prev,
                            candidates: prev.candidates.filter(c => !gone.has(c.candidate_id)),
                        }));
                    }}
                />
            )}
            {addToSeqCandidates && (
                <AddToSequenceModal
                    candidates={addToSeqCandidates}
                    jobId={result?.job_id || jobId}
                    jobRole={result?.job_role || jobRole}
                    onClose={() => setAddToSeqCandidates(null)}
                    onPreview={(seq, list) => {
                        setPreviewState({ candidates: list, sequence: seq });
                        setAddToSeqCandidates(null);
                    }}
                    onEnrolled={(summary) => {
                        setAddToSeqCandidates(null);
                        setSelectedIds(new Set());
                        setEnrollResult(summary);
                        setTimeout(() => setEnrollResult(null), 6000);
                    }}
                />
            )}
            {previewState && (
                <PreviewModal
                    candidates={previewState.candidates}
                    sequence={previewState.sequence}
                    jobId={result?.job_id || jobId}
                    jobRole={result?.job_role || jobRole}
                    onClose={() => setPreviewState(null)}
                    onBack={() => { setAddToSeqCandidates(previewState.candidates); setPreviewState(null); }}
                    onEnroll={(summary) => {
                        setPreviewState(null);
                        setSelectedIds(new Set());
                        setEnrollResult(summary);
                        setTimeout(() => setEnrollResult(null), 6000);
                    }}
                />
            )}
            {enrollResult && (
                <div className={`fixed top-5 right-5 z-[100] flex items-start gap-2 px-4 py-3 bg-white rounded-xl shadow-lg border text-sm max-w-sm ${
                    enrollResult.tone === "error" ? "border-red-200 text-red-700"
                        : enrollResult.tone === "warn" ? "border-amber-200 text-amber-700"
                        : "border-green-200 text-green-700"
                }`}>
                    <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {enrollResult.tone === "success"
                            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />}
                    </svg>
                    {enrollResult.message}
                </div>
            )}
            {toast && (
                <div className="fixed top-5 right-5 z-[100] flex items-center gap-2 px-4 py-3 bg-white dark:bg-[#1B1524] rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300">
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {toast}
                </div>
            )}
            {saveModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-[#1B1524] rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="text-base font-semibold text-gray-800 dark:text-white mb-1">Save Search</h3>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">Give this search a name so you can find it later.</p>
                        <input
                            autoFocus
                            name="search-alias"
                            autoComplete="off"
                            value={aliasInput}
                            onChange={e => setAliasInput(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && handleSaveSearch()}
                            placeholder="e.g. Senior Engineers — Bay Area"
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 mb-4"
                        />
                        {saveError && (
                            <p className="text-xs text-red-500 dark:text-red-400 mb-3">{saveError}</p>
                        )}
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => { setSaveModal(false); setAliasInput(""); setSaveError(""); }}
                                className="px-4 py-2 rounded-full text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveSearch}
                                disabled={!aliasInput.trim()}
                                className="px-4 py-2 rounded-full text-sm font-medium text-white disabled:opacity-50 transition-all"
                                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {editFiltersOpen && result?.parsed_query && (
                <FilterModal
                    initialQuery={result.parsed_query}
                    onClose={() => setEditFiltersOpen(false)}
                    onRunSearch={handleFilterSearch}
                />
            )}
        </div>
    );
};

export default SourcingResults;